import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crosshair, Target, Users, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

const sliderImages = [
  "/assets/values-1.jpg",
  "/assets/values-2.jpg",
  "/assets/values-3.jpg",
  "/assets/values-4.jpg",
];

const values = [
  {
    icon: Crosshair,
    title: "Creative Integrity",
    desc: "Authenticity and originality are the foundation of every project. We believe in creating work that is genuinely innovative and true to brand essence.",
  },
  {
    icon: Target,
    title: "Strategic Clarity",
    desc: "Clear strategy drives exceptional design. We align creative vision with business objectives to deliver measurable results.",
  },
  {
    icon: Users,
    title: "Collaborative Spirit",
    desc: "Partnership is key to success. We work closely with clients as true collaborators, valuing their insights and feedback throughout the journey.",
  },
  {
    icon: Sparkles,
    title: "Innovation",
    desc: "We embrace emerging technologies and trends to push creative boundaries and deliver cutting-edge solutions that stand out.",
  },
];

export default function ValuesSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  // Auto-advance every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrent((prev) => (prev + 1) % sliderImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const goNext = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % sliderImages.length);
  };

  const goPrev = () => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
  };

  return (
    <section id="values" data-testid="values-section" className="py-24 md:py-32 relative">
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#764ba2]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-6 md:px-12 max-w-[1400px] relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Heading + Image Slider */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Philosophy
            </p>
            <h2 className="font-syne text-4xl md:text-6xl font-bold tracking-tight text-white mb-8">
              Our <span className="gradient-text">Values</span>
            </h2>
            <p className="text-base md:text-lg font-light tracking-wide text-zinc-400 mb-10 max-w-[500px]">
              What drives us every single day
            </p>

            {/* Image Slider */}
            <div className="relative overflow-hidden h-[350px] md:h-[450px] rounded-2xl" data-testid="values-slider">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.img
                  key={current}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  src={sliderImages[current]}
                  alt={`DigiStromer showcase ${current + 1}`}
                  className="w-full h-full object-cover absolute inset-0 rounded-2xl"
                />
              </AnimatePresence>

              {/* Slider Controls */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-1">
                  {sliderImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setDirection(i > current ? 1 : -1);
                        setCurrent(i);
                      }}
                      aria-label={`Go to slide ${i + 1}`}
                      data-testid={`values-dot-${i}`}
                      className={`p-2 rounded-full transition-all ${
                        i === current
                          ? "bg-white"
                          : "bg-white/40 hover:bg-white/70"
                      }`}
                      style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <div
                        className={`rounded-full transition-all ${
                          i === current
                            ? "w-2 h-2"
                            : "w-1 h-1"
                        }`}
                        style={{
                          backgroundColor: i === current ? 'transparent' : 'inherit'
                        }}
                      />
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={goPrev}
                    aria-label="Previous slide"
                    data-testid="values-prev-button"
                    className="w-9 h-9 bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={goNext}
                    aria-label="Next slide"
                    data-testid="values-next-button"
                    className="w-9 h-9 bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Values Grid */}
          <div className="grid grid-cols-1 gap-px bg-white/5">
            {values.map((val, i) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="bg-black p-8 group hover:bg-zinc-950/80 transition-all duration-500 border-b border-white/5 last:border-b-0"
                data-testid={`value-card-${i}`}
              >
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 flex items-center justify-center border border-white/10 group-hover:border-[#667eea]/50 transition-colors duration-500">
                    <val.icon size={20} className="text-zinc-500 group-hover:text-[#667eea] transition-colors duration-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-syne text-lg font-semibold text-white mb-3">
                      {val.title}
                    </h3>
                    <p className="text-sm font-light tracking-wide text-zinc-500 leading-relaxed">
                      {val.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
